import { QueryKey, useQuery } from '@tanstack/react-query';
import {
  Flex,
  Text,
  useBreakpointValue,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Button
} from '@chakra-ui/react';
import { ButtonText } from '@morressier/alembic';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown } from '@fortawesome/pro-regular-svg-icons';

import { fetchSubmissionEditorialAssignees } from 'src/api/queries';
import { FETCH_EDITORIAL_ASSIGNEES_KEY } from 'src/constants';
import SingleItem from 'src/layouts/SingleItem';
import SubmissionTab from 'src/components/Submission/SubmissionTab';
import { ISubmissionNotesManagementListView } from '@publishing-engine/domain/views';

import ActivityTab from 'src/components/Submission/ActivityTab/ActivityTab';
import { buildFieldLabelMap } from 'src/components/Submission/ActivityTab/activityLogHandlers';
import IntegrityTab, {
  IIntegrityTab,
  INTEGRITY_TABS
} from 'src/components/Submission/IntegrityTab';

import { Sidebar } from './Sidebar';

import AuthorsTab from 'src/components/Submission/AuthorsTab/AuthorsTab';
import NotesTab from 'src/components/Submission/NotesTab/NotesTab';
import { SubmissionDetailsProvider } from './SubmissionDetailsContext';
import { useMemo, useState } from 'react';
import SubmissionDataProvider, {
  getSubmissionRequestContextFromRoute,
  useSubmissionData
} from '../../contexts/SubmissionDataProvider';
import { IGetSubmissionEditorialAssigneesResponse } from '@publishing-engine/submission-service/src/endpoints/api.response-types';
import BoardMemberReviewsTab, {
  REVIEWS_TABS,
  SubmissionDetailReviewTab
} from 'src/components/Submission/BoardMemberReviewsTab';
import BackofficeTab, {
  BACKOFFICE_TABS,
  IBackofficeTab
} from 'src/components/Submission/BackofficeTab/BackofficeTab';
import { Overlay } from '../../components/Overlay';
import SubmissionReviewsDataProvider from '../../contexts/SubmissionReviewsDataProvider';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import AuthorSubmissionFormDataProvider from 'src/contexts/SubmissionContextProvider';
import COCDataProvider from 'src/contexts/COCDataProvider';
import HeaderInfo from './HeaderInfo';
import { useNextSubmission } from 'src/hooks/useNextSubmission';
import { SubmissionNavContext } from 'src/utils/submissionsList';
import OverviewTab from 'src/components/Submission/OverviewTab';
import NavbarDashboard from 'src/components/NavbarDashboard';
import ScrollToTopButton from '../Organization/public/Journal/SubmissionUpload/components/common/ScrollToTopButton';
import Loading from '../Status/Loading';
import { IRoleType } from '@publishing-engine/domain/models';

export const SUBMISSION_DETAIL_TABS = {
  OVERVIEW: 'overview',
  SUBMISSION: 'submission',
  AUTHORS: 'authors',
  REVIEWS: 'reviews',
  BACKOFFICE: 'backoffice',
  INTEGRITY: 'integrity',
  NOTES: 'notes',
  ACTIVITY: 'activity'
} as const;

export type SubmissionDetailTab =
  (typeof SUBMISSION_DETAIL_TABS)[keyof typeof SUBMISSION_DETAIL_TABS];

type SubmissionDetailsLocationState = {
  from?: string;
  parentFrom?: string;
  submissionNav?: SubmissionNavContext;
};

type RenderTabContent = {
  activeTab: SubmissionDetailTab;
  editorialAssignees: IGetSubmissionEditorialAssigneesResponse['editorialAssignees'];
  notes?: ISubmissionNotesManagementListView | null;
  queryKey: QueryKey;
  noteId?: string;
  submissionId?: string;
  roleContext: IRoleType;
  submissionContextId?: string;
  fieldLabelMap?: Record<string, string>;
};

export const renderTabContent = ({
  activeTab,
  editorialAssignees,
  queryKey,
  submissionId,
  roleContext,
  submissionContextId,
  fieldLabelMap
}: RenderTabContent) => {
  switch (activeTab) {
    case SUBMISSION_DETAIL_TABS.OVERVIEW:
      return <OverviewTab roleContext={roleContext} />;
    case SUBMISSION_DETAIL_TABS.SUBMISSION:
      return <SubmissionTab />;
    case SUBMISSION_DETAIL_TABS.AUTHORS:
      return (
        <AuthorsTab roleContext={roleContext} submissionId={submissionId} />
      );
    case SUBMISSION_DETAIL_TABS.BACKOFFICE:
      return (
        <BackofficeTab
          queryKey={queryKey}
          editorialAssignees={editorialAssignees}
          submissionId={submissionId || ''}
        />
      );
    case SUBMISSION_DETAIL_TABS.NOTES:
      return <NotesTab />;
    case SUBMISSION_DETAIL_TABS.INTEGRITY:
      return <IntegrityTab />;
    case SUBMISSION_DETAIL_TABS.ACTIVITY:
      return (
        <ActivityTab
          roleContext={roleContext}
          submissionContextId={submissionContextId}
          submissionId={submissionId}
          fieldLabelMap={fieldLabelMap}
        />
      );
    case SUBMISSION_DETAIL_TABS.REVIEWS:
      return <BoardMemberReviewsTab roleContext={roleContext} />;
    default:
      return null;
  }
};

const SubmissionDetails = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { shortname } = useParams();

  const defaultTab = new URLSearchParams(location.search).get(
    'activeTab'
  ) as SubmissionDetailTab;

  const [activeTab, setActiveTab] = useState<SubmissionDetailTab>(
    defaultTab ?? SUBMISSION_DETAIL_TABS.OVERVIEW
  );
  const [backOfficeSubTab, setBackOfficeSubTab] = useState<IBackofficeTab>(
    BACKOFFICE_TABS.ROLE_ASSOCIATION
  );
  const [integritySubTab, setBackIntegritySubTab] = useState<IIntegrityTab>(
    INTEGRITY_TABS.INTEGRITY_STATUS
  );
  const [reviewsSubTab, setReviewsSubTab] = useState<SubmissionDetailReviewTab>(
    REVIEWS_TABS.PEER_REVIEWS
  );

  const { workflowResponse, submissionContextId, fieldsResponse } =
    useSubmissionData();
  const { roleContext } = getSubmissionRequestContextFromRoute();
  const { submissionId, articleWorkflow } = workflowResponse;
  const transitionGraph = articleWorkflow?.transitionGraph;
  // TODO replace with useWorkflow() to retrieve Forms + ArticleWorkflow
  const queryKey = ['submission', { submissionId }];

  const { canGoNext, isNextLoading, goNext } = useNextSubmission({
    submissionId,
    activeTab
  });

  const fieldLabelMap = useMemo(
    () =>
      fieldsResponse?.fields
        ? buildFieldLabelMap(fieldsResponse.fields)
        : undefined,
    [fieldsResponse]
  );

  const {
    data: editorialAssigneesResponse,
    isLoading: isLoadingEditorialAssignees
  } = useQuery({
    queryKey: [FETCH_EDITORIAL_ASSIGNEES_KEY, { submissionId }],
    queryFn: () => fetchSubmissionEditorialAssignees(submissionId),
    retry: 0
  });

  if (!transitionGraph) return null;

  const isMobileOrTablet = useBreakpointValue({ base: true, xl: false });
  const tabs = Object.values(SUBMISSION_DETAIL_TABS);

  if (isLoadingEditorialAssignees) {
    return <Loading />;
  }

  const handleTabChange = (tab: SubmissionDetailTab) => {
    setActiveTab(tab);
    navigate(
      `/app/workspace/${shortname}/j/${submissionContextId}/submissions?selectedItem=${submissionId}&activeTab=${tab}`,
      {
        state: location.state
      }
    );
  };

  const onClose = () => {
    const routeState =
      (location.state as SubmissionDetailsLocationState | null) ?? null;
    const from = routeState?.from;
    const parentFrom = routeState?.parentFrom;

    if (from) {
      return navigate(from, {
        state: parentFrom ? { from: parentFrom } : null
      });
    }

    if (location.key !== 'default') {
      return navigate(-1);
    }

    return navigate(`/app/workspace/${shortname}/submissions`);
  };

  return (
    <SubmissionDetailsProvider
      activeTab={activeTab}
      reviewsSubTab={reviewsSubTab}
      backOfficeSubTab={backOfficeSubTab}
      setActiveTab={setActiveTab}
      setReviewsSubTab={setReviewsSubTab}
      setBackOfficeSubTab={setBackOfficeSubTab}
      setBackIntegritySubTab={setBackIntegritySubTab}
      integritySubTab={integritySubTab}
    >
      <NavbarDashboard />
      {/* <HeaderInfo onClose={onClose} /> */}
      <SingleItem bgColor="surfacePrimaryFill">
        {!workflowResponse ? (
          <Flex
            justifyContent="center"
            alignItems="center"
            width="100%"
            height="calc(100vh - 64px)"
            flexDirection="column"
            gap={4}
          >
            <Text textStyle="headingSection">Submission not found</Text>
            {onClose && <ButtonText onClick={onClose}>Close</ButtonText>}
          </Flex>
        ) : (
          <>
            <SingleItem.MainArea>
              <SingleItem.Header>
                <HeaderInfo
                  onClose={onClose}
                  onNext={goNext}
                  isNextEnabled={canGoNext}
                  isNextLoading={isNextLoading}
                />
              </SingleItem.Header>
              <SingleItem.Navigation size="compact">
                {isMobileOrTablet ? (
                  <Menu>
                    <MenuButton
                      as={Button}
                      variant="ghost"
                      rightIcon={<FontAwesomeIcon icon={faChevronDown} />}
                      textTransform="capitalize"
                      fontWeight="semibold"
                      size="sm"
                      px={4}
                    >
                      {activeTab}
                    </MenuButton>
                    <MenuList zIndex={10}>
                      {tabs.map(tab => (
                        <MenuItem
                          key={tab}
                          onClick={() => handleTabChange(tab)}
                          textTransform="capitalize"
                          fontWeight={activeTab === tab ? 'semibold' : 'normal'}
                          bg={activeTab === tab ? 'blue.50' : undefined}
                        >
                          {tab}
                        </MenuItem>
                      ))}
                    </MenuList>
                  </Menu>
                ) : (
                  tabs.map(tab => (
                    <SingleItem.Tab
                      key={tab}
                      onClick={() => handleTabChange(tab)}
                      isActive={activeTab === tab}
                    >
                      {tab}
                    </SingleItem.Tab>
                  ))
                )}
              </SingleItem.Navigation>
              <SingleItem.Content size="default" bgColor="surfacePrimaryFill">
                {renderTabContent({
                  activeTab,
                  editorialAssignees:
                    editorialAssigneesResponse!.editorialAssignees,
                  queryKey,
                  submissionId,
                  roleContext,
                  submissionContextId,
                  fieldLabelMap
                })}
              </SingleItem.Content>
            </SingleItem.MainArea>

            <SingleItem.Sidebar width="100%">
              <Sidebar
                setActiveTab={setActiveTab}
                onClose={onClose}
                editorialAssignees={
                  editorialAssigneesResponse!.editorialAssignees
                }
                queryKey={queryKey}
                activeTab={activeTab}
              />
            </SingleItem.Sidebar>
          </>
        )}
      </SingleItem>
    </SubmissionDetailsProvider>
  );
};

const Page = () => {
  return (
    <Overlay id="scrollable-wrapper" overflowY="auto" height="100%">
      <SubmissionDataProvider>
        <AuthorSubmissionFormDataProvider>
          <SubmissionReviewsDataProvider>
            <COCDataProvider>
              <SubmissionDetails />
            </COCDataProvider>
          </SubmissionReviewsDataProvider>
        </AuthorSubmissionFormDataProvider>
      </SubmissionDataProvider>
      <ScrollToTopButton />
    </Overlay>
  );
};

export default Page;
